import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh, GetApp, HelpOutline } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { commonAPI, rentalAPI } from '../../services/api';
import { DepartmentOption } from '../../services/api';
import { FeeDetailsFilters, FeeDetailsRow, PaginatedResponse } from '../../types/rental';

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatDateHour = (value?: string) => {
  if (!value) return '-';
  // 資料庫已是台灣時間，直接格式化字串顯示，不做 Date 轉換
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))/);
  if (m) return `${m[1]}/${m[2]}/${m[3]} - ${m[4]}:${m[5]}`;
  return value;
};

const toYMD = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const dateFieldSx = {
  width: '100%',
  minWidth: 0,
  maxWidth: 280,
  '& .MuiOutlinedInput-root': {
    height: 56,
  },
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    height: '100%',
    paddingTop: '16.5px',
    paddingBottom: '16.5px',
  },
} as const;

const assetKindLabel = (kind?: string) => {
  if (kind === 'equipment') return '\u6a5f\u5177';
  if (kind === 'vehicle') return '\u8eca\u8f1b';
  return kind || '-';
};

const FeeDetailsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<FeeDetailsRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [departmentId, setDepartmentId] = useState<number | ''>('');

  const getDefaultRange = () => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 30);
    return { date_from: toYMD(start), date_to: toYMD(end) };
  };

  const defaultRange = useMemo(() => getDefaultRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.date_from);
  const [dateTo, setDateTo] = useState(defaultRange.date_to);

  const dateRangeError = useMemo(
    () => !!(dateFrom && dateTo && dateFrom > dateTo),
    [dateFrom, dateTo]
  );

  const exportToExcel = () => {
    const headers = [
      '序號',
      '類型',
      '設備資訊',
      '部門',
      '開始使用',
      '結束使用',
      '起始里程',
      '結束里程',
      '使用里程',
      '用途',
      '總金額（預估）',
    ];

    const data = rows.map((r, idx) => {
      const equipInfo = `${r.equipment_name || '-'}${r.license_plate ? ` ${r.license_plate}` : ''}`;
      return [
        idx + 1,
        assetKindLabel(r.asset_kind),
        equipInfo,
        r.department_name || `部門ID:${r.department_id}`,
        formatDateHour(r.start_time),
        formatDateHour(r.end_time),
        r.mileage_start != null ? Number(r.mileage_start) : '',
        r.mileage_end != null ? Number(r.mileage_end) : '',
        typeof r.mileage_used === 'number' ? r.mileage_used : '',
        r.purpose || '',
        typeof r.total_amount_estimated === 'number' ? r.total_amount_estimated : '',
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    ws['!cols'] = [
      { wch: 6 },
      { wch: 8 },
      { wch: 24 },
      { wch: 12 },
      { wch: 20 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 28 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '費用明細');

    const now = new Date();
    const filename = `費用明細_${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(now.getHours())}${pad2(now.getMinutes())}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const fetchFeeDetails = async () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      const filters: FeeDetailsFilters = {
        date_from: dateFrom,
        date_to: dateTo,
        department_id: departmentId === '' ? undefined : departmentId,
      };
      const response: PaginatedResponse<FeeDetailsRow> = await rentalAPI.getFeeDetails(filters);
      setRows(response.results || []);
      setTotalCount(response.count || 0);
    } catch (e) {
      console.error('\u8f09\u5165\u8cbb\u7528\u660e\u7d30\u5931\u6557:', e);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const list = await commonAPI.getDepartments();
        setDepartments(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('\u8f09\u5165\u90e8\u9580\u5931\u6557:', e);
        setDepartments([]);
      }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    fetchFeeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, dateFrom, dateTo]);

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            gap={2}
            flexWrap="wrap"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '1.2rem', alignSelf: 'center' }}
              >
                {`共 ${totalCount} \u7b46`}
              </Typography>
              <Tooltip
                title={
                  '\u958b\u59cb\u65e5\u671f\u8207\u7d50\u675f\u65e5\u671f\u4f9d\u8a02\u55ae\u4f7f\u7528\u7d00\u9304\u7684\u958b\u59cb\u6642\u9593\u7be9\u9078'
                }
                arrow
                slotProps={{ tooltip: { sx: { fontSize: '0.95rem', px: 1.25, py: 0.8 } } }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={'\u65e5\u671f\u7bc4\u570d\u8aaa\u660e'}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: '0 1 160px', minWidth: { xs: '100%', sm: 160 } }}>
                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { height: 56 } }}>
                  <InputLabel>部門</InputLabel>
                  <Select
                    value={departmentId}
                    label="部門"
                    onChange={(e) => {
                      const raw = e.target.value as number | '';
                      setDepartmentId(raw);
                    }}
                  >
                    <MenuItem value="">全部</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 230px', minWidth: { xs: '100%', sm: 220 }, maxWidth: { sm: 280 } }}>
                <TextField
                  fullWidth
                  label="開始日期"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={dateRangeError}
                  FormHelperTextProps={{
                    sx: { minHeight: '1.25rem', marginTop: 0.5, marginBottom: 0 },
                  }}
                  helperText=" "
                  sx={dateFieldSx}
                />
              </Box>

              <Box sx={{ flex: '1 1 230px', minWidth: { xs: '100%', sm: 220 }, maxWidth: { sm: 280 } }}>
                <TextField
                  fullWidth
                  label="結束日期"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={dateRangeError}
                  inputProps={{ min: dateFrom || undefined }}
                  FormHelperTextProps={{
                    sx: { minHeight: '1.25rem', marginTop: 0.5, marginBottom: 0 },
                  }}
                  helperText={
                    dateRangeError ? '結束日期不能早於開始日期' : ' '
                  }
                  sx={dateFieldSx}
                />
              </Box>

              <Box
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  pt: { xs: 0, sm: 0 },
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<Refresh />}
                  onClick={fetchFeeDetails}
                  disabled={loading}
                  sx={{
                    height: '56px',
                    whiteSpace: 'nowrap',
                    borderColor: 'success.main',
                    '&:hover': {
                      borderColor: 'success.dark',
                      backgroundColor: 'rgba(76,175,80,0.08)',
                    },
                  }}
                >
                  重新整理
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<GetApp />}
                  onClick={exportToExcel}
                  disabled={loading}
                  sx={{
                    height: '56px',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#43a047',
                    '&:hover': {
                      backgroundColor: '#2e7d32',
                    },
                  }}
                >
                  匯出 Excel
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer
            sx={{
              border: '2px solid rgba(67, 160, 71, 0.35)',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
            }}
          >
            <Table
              sx={{
                '& .MuiTableCell-root': {
                  textAlign: 'center',
                  border: '1px solid rgba(0,0,0,0.12)',
                  fontSize: '1.1rem',
                  padding: '16px 8px',
                },
                '& .MuiTableCell-head': {
                  textAlign: 'center',
                  border: '1px solid rgba(0,0,0,0.2)',
                  fontSize: '1.15rem',
                  fontWeight: 'bold',
                  backgroundColor: '#43a047',
                  color: '#ffffff',
                },
                '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
                  backgroundColor: 'rgba(67, 160, 71, 0.04)',
                },
                '& .MuiTableBody-root .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(67, 160, 71, 0.12)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{'\u5e8f\u865f'}</TableCell>
                  <TableCell>{'\u985e\u578b'}</TableCell>
                  <TableCell>{'\u8a2d\u5099\u8cc7\u8a0a'}</TableCell>
                  <TableCell>{'\u90e8\u9580'}</TableCell>
                  <TableCell>{'\u958b\u59cb\u4f7f\u7528'}</TableCell>
                  <TableCell>{'\u7d50\u675f\u4f7f\u7528'}</TableCell>
                  <TableCell>{'\u8d77\u59cb\u91cc\u7a0b'}</TableCell>
                  <TableCell>{'\u7d50\u675f\u91cc\u7a0b'}</TableCell>
                  <TableCell>{'\u4f7f\u7528\u91cc\u7a0b'}</TableCell>
                  <TableCell>{'\u7e3d\u91d1\u984d\uff08\u9810\u4f30\uff09'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="text.secondary">
                        {'\u5c1a\u7121\u8cbb\u7528\u660e\u7d30'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, idx) => (
                    <TableRow key={`${r.usage_id}`}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {assetKindLabel(r.asset_kind)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {r.equipment_name || '-'}
                          {r.license_plate ? ` ${r.license_plate}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {r.department_name ||
                            `\u90e8\u9580ID:${r.department_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDateHour(r.start_time)}</TableCell>
                      <TableCell>{formatDateHour(r.end_time)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {r.mileage_start != null ? Number(r.mileage_start).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {r.mileage_end != null ? Number(r.mileage_end).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {typeof r.mileage_used === 'number' ? r.mileage_used.toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
                          {typeof r.total_amount_estimated === 'number'
                            ? r.total_amount_estimated.toLocaleString()
                            : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeeDetailsPage;
